<template>
  <div>
    <transition name="fade">
      <div class="be-mapmodal__dim" v-if="isOpen" @click="close"></div>
    </transition>

    <transition :name="$is_mobile_view ? 'be-sheet' : 'be-modal'">
      <div class="be-mapmodal__wrap" v-if="isOpen">
        <div class="be-mapmodal__panel">
          <div class="be-mapmodal__head">
            <!-- taken from the map pin, so the panel has a title from the first
                 frame instead of waiting on the search request -->
            <p class="be-mapmodal__title mb-0 text-truncate">{{ roomTypeName }}</p>
            <img
              src="../../../../assets/img/icon/cancel.png"
              class="be-mapmodal__close be-press d-inline-block p-2 rounded-3 bg-back cursor-pointer"
              width="33"
              alt=""
              @click="close">
          </div>

          <div class="be-mapmodal__body">
            <BeRoomCardSkeleton v-if="is_loading" :count="1" />

            <template v-else-if="$isNotEmptyV2(room_type)">
              <!-- The room IS sold here, it is only gone for these dates, so
                   alternatives are worth offering. Keyed on the stay dates: the
                   carousel fetches once on mount and reads the filter from the
                   store, so a reload on new dates has to remount it or it would
                   keep showing the old suggestions. -->
              <FlexibleDateAlternatives
                v-if="isRoomSoldOut"
                class="mb-3"
                :key="flexibleDatesKey"
                :room-type-id="roomTypeId"
                :title="$t('hotel.map-flexible-dates-title')"
                @select-date="$emit('select-date', $event)" />

              <BeRoomCard
                :type="room_type"
                @room_type_detail="onRoomTypeDetail"
                @toggleRateBreakdown="onRateBreakdown"
                @toggleBookingRateCart="onBookingRateCart"
                @handlePopupFilter="onPopupFilter" />
            </template>

            <!-- Not in the search results at all: the room is not on sale, which
                 no other date can fix -- show what the map knows about it and
                 leave it there. -->
            <BeMapRoomUnavailable
              v-else
              :room-type-id="roomTypeId"
              :merchant-id="searchParams.merchant_id"
              :message="error_message || $t('hotel.map-room-unavailable')" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { mapMutations } from 'vuex';
  import { get } from '@/services/index.js';
  import { withIndexedRateIds } from '@/utils/beRoomType.js';
  import BeRoomCard from './BeRoomCard.vue';
  import BeRoomCardSkeleton from './BeRoomCardSkeleton.vue';
  import FlexibleDateAlternatives from '@/components/hotel/be/FlexibleDateAlternatives.vue';
  import BeMapRoomUnavailable from './BeMapRoomUnavailable.vue';

  export default {
    name: 'BeMapRoomModal',
    components: {
      BeRoomCard,
      BeRoomCardSkeleton,
      FlexibleDateAlternatives,
      BeMapRoomUnavailable
    },
    props: {
      roomTypeId: {
        type: [String, Number],
        default: null
      },
      // the pin's label, shown while the rates are still loading
      roomTypeName: {
        type: String,
        default: ''
      },
      // the page's current search-room params; room_type_id is added on top
      searchParams: {
        type: Object,
        default: () => ({})
      }
    },
    data(){
      return {
        room_type: null,
        is_loading: false,
        error_message: ''
      }
    },
    computed: {
      isOpen(){
        return this.$isNotEmptyV2(this.roomTypeId);
      },
      /* One computed rather than a watcher per prop: the page sets both the
         params and the room type in the same tick when a popup opens, and two
         watchers would each fire a search for it. */
      loadSignature(){
        if(!this.$isNotEmptyV2(this.roomTypeId)) return '';
        return this.roomTypeId + '|' + JSON.stringify(this.searchParams);
      },
      flexibleDatesKey(){
        const { check_in_date, check_out_date } = this.searchParams;
        return this.roomTypeId + '_' + check_in_date + '_' + check_out_date;
      },
      /* Same rule the list view applies per page (isAllRoomsSoldOut), narrowed
         to the one room type this panel shows. A rate the guest cannot book --
         closed out or with nothing left -- counts as gone for these dates. */
      isRoomSoldOut(){
        if(!this.$isNotEmptyV2(this.room_type)) return false;
        const rates = this.room_type.room_rate || [];
        if(rates.length == 0) return true;
        return rates.every((rate) => !rate.bookable || this.$roomAvbl(rate.room_type.id, rate.id) <= 0);
      }
    },
    watch: {
      isOpen(isOpen){
        if(isOpen) this.lockPage();
        else this.unlockPage();
      },
      loadSignature: {
        immediate: true,
        handler(loadSignature){
          if(!loadSignature){
            this.room_type = null;
            this.error_message = '';
            return;
          }
          this.loadRoomType(this.roomTypeId);
        }
      }
    },
    created(){
      // plain instance property: restoring an inline style is not view state
      this.previous_body_overflow = '';
      // request sequence number, so a slow response for a popup the guest has
      // already closed cannot paint into the next one they open
      this.load_request_token = 0;
    },
    mounted(){
      if(this.isOpen) this.lockPage();
    },
    beforeDestroy(){
      this.unlockPage();
    },
    methods: {
      ...mapMutations(['mergeBeRoomAvbl']),
      /* Runs on every open, never from a cached snapshot: the map itself is
         built from room-type, which knows nothing about the guest's dates,
         so this filtered search is the only place real rates and availability
         for the picked room come from. */
      async loadRoomType(roomTypeId){
        this.load_request_token += 1;
        const requestToken = this.load_request_token;

        this.is_loading = true;
        this.room_type = null;
        this.error_message = '';

        try {
          const apiParams = Object.assign({}, this.searchParams, {
            room_type_id: roomTypeId,
          });
          const apiResponse = await get('CORE_HOTEL', 'search-room', apiParams);
          if(requestToken != this.load_request_token) return;

          const { success, message, data, room_type_availability } = apiResponse.data;
          if(!success) throw new Error(message);

          /* Merge, never replace: the payload only covers this one room type,
             and setBeRoomAvbl would drop the availability of every other one
             along with their cart rows. */
          const availability = (room_type_availability || []).map((roomType) => {
            roomType.original_availability = roomType.availability;
            return roomType;
          });
          this.mergeBeRoomAvbl(availability);

          this.room_type = data && data.length > 0 ? withIndexedRateIds(data[0]) : null;
        } catch (error) {
          if(requestToken != this.load_request_token) return;
          this.error_message = error.message;
        } finally {
          if(requestToken == this.load_request_token) this.is_loading = false;
        }
      },
      lockPage(){
        this.previous_body_overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
      },
      unlockPage(){
        document.body.style.overflow = this.previous_body_overflow || '';
        document.removeEventListener('keydown', this.onKeydown);
      },
      onKeydown(e){
        if(e.key === 'Escape' || e.key === 'Esc') this.close();
      },
      close(){
        this.$emit('close');
      },
      onRoomTypeDetail(type){
        this.$emit('room_type_detail', type);
      },
      onRateBreakdown(type, data){
        this.$emit('toggleRateBreakdown', type, data);
      },
      // BeRoomCard emits (type, data, qty) -- the event name is its own first
      // argument and is already consumed by the $emit that raised this
      onBookingRateCart(type, data, qty){
        this.$emit('toggleBookingRateCart', type, data, qty);
        // BeRoomCard.addToCartOne() has already committed the cart mutation by
        // the time this fires, so 'add' here means the add-to-cart succeeded --
        // close the popup instead of leaving the guest staring at the same room.
        if(type == 'add') this.close();
      },
      onPopupFilter(show){
        this.$emit('handlePopupFilter', show);
      }
    }
  }
</script>

<style scoped>
/* Rendered inside .be-landing rather than through b-modal on purpose: b-modal
   portals to <body>, which would drop the merchant radius/font vars this page
   sets inline on its root. Sitting below roomTypeDetail (1045) is also
   deliberate -- the "detail" button inside the card must open that popup on top
   of this one. */
.be-mapmodal__dim {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1042;
  background: rgba(0, 0, 0, 0.5);
}
/* The panel is centred by this wrapper rather than by its own transform: the
   be-modal / be-sheet transitions animate transform, and a centring transform
   here would be overwritten mid-animation (same trap the .be-pop-down-center
   variant in be-v3-motion.css exists to work around). */
.be-mapmodal__wrap {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1043;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* clicks between the panel and the screen edge fall through to the dim, which
     closes -- so the wrapper needs no close handler of its own */
  pointer-events: none;
}
.be-mapmodal__panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1080px;
  max-height: 90vh;
  background: #fff;
  border-radius: var(--be-v3-radius, 12px);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
  overflow: hidden;
  pointer-events: auto;
}
.be-mapmodal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--be-v3-border-soft, #e3e6eb);
}
.be-mapmodal__title {
  font-family: var(--be-v3-title-font, BeV3Sans);
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a2e;
}
.be-mapmodal__close {
  flex-shrink: 0;
}
.be-mapmodal__body {
  padding: 16px 20px;
  overflow-y: auto;
}
/* the card is the only thing in the scroll area, so its list spacing would just
   read as a stray gap above the panel's edge */
.be-mapmodal__body >>> .be-room-card {
  margin-bottom: 0;
}
/* The sidebar sticks against the page navbar (top: 100px). Inside this panel's
   own scroller that offset only tears it away from the rate column the moment
   Rate Detail makes the column taller. */
.be-mapmodal__body >>> .be-room-card__sidebar {
  position: static;
}
@media screen and (max-width: 767.98px) {
  .be-mapmodal__wrap {
    align-items: flex-end;
    padding: 0;
  }
  .be-mapmodal__panel {
    max-width: none;
    max-height: 92vh;
    border-radius: var(--be-v3-radius, 16px) var(--be-v3-radius, 16px) 0 0;
  }
  .be-mapmodal__head {
    padding: 14px 16px;
  }
  .be-mapmodal__body {
    padding: 12px 16px 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeMapRoomModal.vue